<template>
  <div>
    <b-form-row class="mt-3 mb-2">
      <b-col cols="12">
        <b-button
          class="mb-2 mb-sm-0 mr-sm-2 btn-responsive"
          size="sm"
          variant="primary"
          @click="toggleSelectAll"
        >
          {{ selectAll ? 'Deselect all rows' : 'Select all rows' }}
        </b-button>

        <b-button
          class="mb-2 mb-sm-0 btn-responsive"
          size="sm"
          variant="danger"
          :disabled="!hasSelection"
          @click="excludeSelectedRows"
        >
          Exclude selected rows
        </b-button>
        <label class="siteRowsStyle"> {{ getRowCount }} </label>
        <label class="excludedRowsStyle"> {{ numberOfExcludedRowsText }} </label>
      </b-col>
    </b-form-row>
    <b-table
      id="table-rows"
      class="mb-2 mt-2"
      :bordered="true"
      :striped="true"
      :head-variant="'dark'"
      :per-page="perPage"
      :current-page="currentPage"
      hover
      sort-icon-left
      :items="items"
      :fields="fields"
      :small="true"
      :responsive="true"
      primary-key="rowNum"
      sort-compare-locale="sv"
      :sort-desc.sync="sortDesc"
      :sort-by.sync="sortBy"
      no-sort-reset
      :no-local-sorting="true"
      :busy="isBusy"
      @row-clicked="rowClicked"
      @sort-changed="onSort"
    >
      <template
        v-if="sumVisible"
        v-slot:top-row
      >
        <td :colspan="sumArrayColspan">
          <div class="sumrowtop">
            <div
              v-for="(sum, iSum) in summaryArray"
              :key="iSum"
              class="sumrowdiv"
            >
              <span>{{ sum.name }}:</span>
            </div>
          </div>
        </td>
        <td
          v-for="(measure, iMeasure) in measuresInTable"
          :key="iMeasure"
          class="text-right"
          colspan="1"
        >
          <div
            v-for="(sum, iSum) in summaryArray"
            :key="iSum"
            class="sumrowdiv"
          >
            {{ sum.measures[measure.key] }}
          </div>
        </td>
      </template>
      <template v-slot:cell(valda)="row">
        <template v-if="row.value">
          <b-checkbox
            v-model="row.value"
            class="avoid-clicks"
            aria-hidden="true"
          />
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <b-checkbox
            v-model="row.value"
            class="avoid-clicks"
            aria-hidden="true"
          />
          <span class="sr-only">Not selected</span>
        </template>
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
    />
  </div>
</template>

<script>
import { dateToyymmddFormat, addDays, daysBetween } from '../../../assets/js/utils.js';
import { bus } from '../../../main';
import Vue from 'vue';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';

Vue.use(Toast, {
  transition: 'Vue-Toastification__bounce',
  maxToasts: 20,
  newestOnTop: true,
});

export default {
  name: 'SiteResultTable',
  data() {
    return {
      loadingSearchResults: false,
      currentPage: 1,
      selectAll: false,
      latestRowClicked: null,
      shiftKey: false,
      totalRows: null,
      isBusy: false,
      perPage: 50
    };
  },
  computed: {
    sumVisible() {
      return this.$store.getters[`siteResults/getSumVisible`];
    },
    items() {
      return this.$store.state.siteResults.results;
    },
    fields() {
      return this.$store.state.siteResults.resultFields.filter(z => z.visible == true);
      //return this.$store.state.siteResults.resultFields;
    },
    rows() {
      return this.$store.state.siteResults.results.length;
    },
    selectedRows() {
      return this.$store.state.siteResults.results.filter((item) => item.valda).length;
    },
    hasSelection() {
      return this.$store.state.siteReachApiBody.payload.queryKeys.length > 0;
    },
    getRowCount() {
      let previousRowsAndCurrentRows = this.perPage * this.currentPage
      if (previousRowsAndCurrentRows > this.rows) {
        return `${this.rows}/${this.rows}`
      }
      return `${previousRowsAndCurrentRows}/${this.rows}`
    },
    sortBy: {
      get() {
        return this.$store.state.siteApiBody.payload.sort_by;
      },
      set(value) {
        return value;
      },
    },
    sortDesc: {
      get() {
        return this.$store.state.siteApiBody.payload.sort_direction == 'DESC';
      },
      set(value) {
        return value;
      },
    },
    numberOfExcludedRowsText() {
      let excludedRowsText = "";
      let numberOfExcludedRows = this.$store.state.siteResults.totalRowCount - this.rows
      if(numberOfExcludedRows > 0)
        excludedRowsText = `(${numberOfExcludedRows} excluded)`
      return excludedRowsText;
    },
    measuresInTable() {
      return this.$store.getters[`siteResults/getMeasuresInTable`];
    },
    summaryArray() {
      return this.$store.state.siteResults.summaryArray;
    },
    sumArrayColspan() {
      return this.fields.filter(f => f.tdClass !== "d-none" && !f.isMeasure).length
    }
  },
  mounted() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Shift') {
        this.shiftKey = true;
      }
    });

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') {
        this.shiftKey = false;
      }
    });

    bus.$on('searching', () => {
      this.selectAll = false;
    });
  },
  methods: {
    formatDate(date) {
      return dateToyymmddFormat(date);
    },
    rowClicked(item) {
      const currentIndex = this.$store.state.siteResults.results.findIndex(
        element => element.rowNum === item.rowNum
      );
      const rowSelected = this.$store.state.siteResults.results[currentIndex]?.valda;

      if (this.shiftKey && this.latestRowClicked !== null) {
        const lastClickedIndex = this.$store.state.siteResults.results.findIndex(
          element => element.rowNum === this.latestRowClicked
        );

        const minIndex = Math.min(currentIndex, lastClickedIndex);
        const maxIndex = Math.max(currentIndex, lastClickedIndex);
        
        // Check if we're selecting or deselecting based on the clicked row's state
        const targetState = !rowSelected;

        // Apply the same state to all rows in the range
        for (let i = minIndex; i <= maxIndex; i++) {
          const rowNum = this.$store.state.siteResults.results[i].rowNum;
          if (targetState) {
            this.$store.commit('siteResults/select', rowNum);
          } else {
            this.$store.commit('siteResults/deselect', rowNum);
          }
        }
      } else {
        // Single row toggle
        if (rowSelected) {
          this.$store.commit('siteResults/deselect', item.rowNum);
        } else {
          this.$store.commit('siteResults/select', item.rowNum);
        }
        
        this.latestRowClicked = item.rowNum;
      }

      this.updateReachApiBodySite();
    },
    async onSort(item) {
      this.isBusy = true;
      await this.$store.commit('siteApiBody/updateSortBy', item.sortBy);
      await this.$store.commit('siteApiBody/updateSortDirection', item.sortDesc ? 'DESC' : 'ASC');
      this.$emit('sort-changed');
      this.isBusy = false;
    },
    excludeSelectedRows() {
      this.latestRowClicked = null;
      const selectedRows = this.$store.state.siteResults.results
        .filter(item => item.valda)
        .map(item => item.rowNum);
      
      this.$emit('exclude-rows', selectedRows);
    },
    toggleSelectAll() {
      this.latestRowClicked = null;
      if (!this.selectAll) {
        this.$store.commit('siteResults/selectAll');
        this.selectAll = true;

        this.$store.state.siteResults.results.filter(function(obj) {
          return obj.valda === true;
        });
      } else {
        this.$store.commit('siteResults/unselectAll');
        this.selectAll = false;
      }
      this.updateReachApiBodySite();
    },
    updateReachApiBodySite() {
      let queryKeys = [];

      let minDate = '';
      let maxDate = '';

      this.items.forEach((element) => {
        if (element.valda === true) {
          let date = new Date(element.consumed_at);

          if (minDate) {
            let minDateCompare = new Date(minDate);

            if (date < minDateCompare) {
              minDate = element.consumed_at;
            }
          } else {
            minDate = element.consumed_at;
          }

          if (maxDate) {
            let maxDateCompare = new Date(maxDate);

            if (date > maxDateCompare) {
              maxDate = element.consumed_at;
            }
          } else {
            maxDate = element.consumed_at;
          }
        }
      });

      let days = daysBetween(minDate, maxDate);
      //Max days to transfere to reach is 28
      if (days < this.config_MaxReachDays) {
        this.$store.commit('siteReachApiBody/updateAutoChangeEndDate', false);
        this.items.forEach((element) => {
          if (element.valda === true) {
            let queryKey = element.site_id + ';' + element.consumed_at;
            queryKeys.push(queryKey);
          }
        });
      } else {
        //adjust the result to match 28 days
        maxDate = addDays(minDate, this.config_MaxReachDays - 1);
        this.$store.commit('siteReachApiBody/updateAutoChangeEndDate', true);
        //Change date for queryKey
        this.items.forEach((element) => {
          if (element.valda === true) {
            let queryKey = '';
            if (element.consumed_at <= maxDate) {
              queryKey = element.site_id + ';' + element.consumed_at;
            } else {
              queryKey = element.site_id + ';' + maxDate;
            }
            queryKeys.push(queryKey);
          }
        });
      }

      this.$store.commit('siteReachApiBody/updateQueryKeys', queryKeys);
      this.$store.commit('siteReachApiBody/updateStartDate', minDate);
      this.$store.commit('siteReachApiBody/updateEndDate', maxDate);
    },
  },
};
</script>
<style scoped>
.siteRowsStyle {
  margin-left: 10px;
  margin-top: 8px;
}
.pagination {
  justify-content: center;
}
</style>
